/**
 * RESOLVING THE AUTHORED CONFIG INTO THE ONE THE FIELDS READ.
 *
 * `WorldGenConfig` is the tree: what the editor renders, what a world stores, what goes on the wire.
 * It is a `Record<kind, {enabled, params}>`, which is the right shape for all three of those and the
 * WRONG one to read per sample — every access is a Map lookup and an object walk, in code that asks
 * for config once per column.
 *
 * So the tree is resolved ONCE, at config time, into the flat typed structs the fields already take.
 * That is not the tree failing to be the architecture; it is the same relationship a compiled
 * program has to its source. The authored shape is the tree, and this is where it is compiled.
 *
 * The direction of the arrow matters: nothing writes back. A field that wants a new knob declares it
 * in the catalog and reads it here — it never reaches into `WorldGenConfig` itself.
 */
import { DEFAULT_REGION_CONFIG, RegionType } from '../regions/index.js';
import { DEFAULT_STAMP_DISTRIBUTION } from '../stamps/index.js';
import { StampType } from '../stamps/StampTypes.js';
import { descriptor, getNumber, getParam } from '../nodes/index.js';
import { mat } from '../../materials/index.js';
import { VOXEL_SCALE } from '../../voxel/constants.js';
import { DEFAULT_TERRAIN_LAYER_CONFIG } from '../TerrainGenerator.js';
/**
 * A NODE'S OWN SWITCH, NOT THE ANCESTOR CHAIN — and the difference is a bug, not a nicety.
 *
 * `isEnabled` walks up the tree, which is right for the EDITOR (a child of a disabled parent is
 * inert and greys). It is wrong here, because the flat config does its own nesting: `regionConfig`
 * has an `enabled` of its own that the generator already uses to gate everything inside it, so
 * asking the chain would count the parent twice. Worse for the scatter, which sits under `meadow`
 * under `regions` in the catalog but is NOT region-gated at runtime when there is no region field —
 * `isEnabled` there turned every tree in the world off the moment regions were off.
 */
const own = (cfg, kind) => cfg.nodes[kind]?.enabled ?? true;
/**
 * THE TWO UNIT CONVERSIONS, AND THEY LIVE HERE FOR THE SAME REASON EVERYTHING ELSE DOES.
 *
 * The catalog declares what the PLAYER sets — a beach that rises 0.5% of the relief, a wall 10 m
 * tall — and the fields read what the GENERATOR works in: a fraction, and fine voxels. Declaring
 * the generator's units in the dialog is how it came to ask for "0.0050" with a step of 0.0025,
 * and a knob whose whole range prints as 0.00 is not a knob.
 *
 * NEVER ROUNDED. A percent is a float: beach width steps by a quarter of a percent and its default
 * is a half, and an `Math.round` anywhere on this path turns that into nothing at all.
 */
const pct = (v) => v / 100;
const vox = (v) => v / VOXEL_SCALE;
/** A material parameter, resolved to the id the generator reads. Names are the authored shape. */
function material(cfg, kind, key, fallback) {
    const v = getParam(cfg, kind, key);
    return mat((typeof v === 'string' ? v : fallback));
}
/**
 * THE REGION TYPES, IN TYPE_ORDER — the one place the tree's region-type nodes meet the field's
 * flat arrays. `typeWeights` is each node's own `frequency`, zeroed when its tickbox is off, and
 * `scatterRegions` is derived rather than declared: a type scatters where it HAS enabled scatter
 * children, which is meadow today and whatever gains stamp children tomorrow.
 *
 * A FOURTH REGION TYPE ADDS ITSELF HERE AND NOWHERE ELSE, which is the point of the exercise.
 */
const REGION_TYPE_KINDS = [
    { type: RegionType.MEADOW, kind: 'meadow', fallbackWall: 'stone' },
    { type: RegionType.WILD, kind: 'wild', fallbackWall: 'stone2' },
    { type: RegionType.CITY, kind: 'city', fallbackWall: 'concrete' },
];
/**
 * THE STAMP LEAVES, AND THE PRIMARY TYPE EACH ONE STANDS FOR — the second and last place the tree
 * meets a flat list the fields read. `trees` is one distribution drawing oaks AND pines from a type
 * pool, which is why it maps to a single primary type rather than to the two it can produce.
 *
 * `priority` and `typePool` are NOT config: priority is the order the exclusion rules run in (towers
 * claim space before huts before rocks before trees) and changing it is a generation decision, not a
 * knob. Both come from the default entry for the type.
 */
const STAMP_KINDS = [
    { kind: 'trees', type: StampType.TREE_OAK },
    { kind: 'oaksLarge', type: StampType.TREE_OAK_LARGE },
    { kind: 'rockSmall', type: StampType.ROCK_SMALL },
    { kind: 'rockMedium', type: StampType.ROCK_MEDIUM },
    { kind: 'rockLarge', type: StampType.ROCK_LARGE },
    { kind: 'buildingHut', type: StampType.BUILDING_HUT },
    { kind: 'buildingSmall', type: StampType.BUILDING_SMALL },
    { kind: 'buildingTower', type: StampType.BUILDING_TOWER },
];
/**
 * One distribution per ticked stamp kind, carrying that kind's own numbers.
 *
 * A KIND THAT IS OFF IS ABSENT, not zero-density: the point generator walks the list, so dropping
 * the entry is both the cheapest and the only unambiguous way to say it. This is what makes the
 * seven tickboxes and their twenty-one sliders mean anything — until it existed, `applyTerrainLayer`
 * rebuilt the list from the defaults on every config change and every one of them was inert.
 */
function stampDistributions(cfg) {
    const out = [];
    for (const { kind, type } of STAMP_KINDS) {
        if (!own(cfg, kind))
            continue;
        const base = DEFAULT_STAMP_DISTRIBUTION.distributions.find((d) => d.type === type);
        if (!base)
            continue;
        out.push({
            ...base,
            gridSize: getNumber(cfg, kind, 'gridSize'),
            jitter: pct(getNumber(cfg, kind, 'jitter')),
            exclusionRadius: getNumber(cfg, kind, 'exclusionRadius'),
            coverage: pct(getNumber(cfg, kind, 'coverage')),
        });
    }
    return out;
}
/** True when this kind has at least one enabled child that draws a stamp. */
function scatters(cfg, kind) {
    const children = descriptor(kind)?.children ?? [];
    return children.some((c) => own(cfg, c) && (descriptor(c)?.capability === 'volume'));
}
export function resolveGenConfig(cfg) {
    // The height model IS the landform tickbox: unticked is the flat world, which is why there is no
    // `flat` kind and no enum choosing between the two.
    const flat = !own(cfg, 'landform');
    const n = (kind, key) => getNumber(cfg, kind, key);
    const stamps = own(cfg, 'meadow') && scatters(cfg, 'meadow');
    const terrain = {
        ...DEFAULT_TERRAIN_LAYER_CONFIG,
        stampsEnabled: stamps,
        flatEnabled: flat,
        groundLevel: vox(n('world', 'groundLevel')),
        masterScale: n('world', 'masterScale'),
        landScale: n('landform', 'landScale'),
        landformEnabled: !flat,
        seaCoveragePercent: n('landform', 'seaCoveragePercent'),
        landformScale: n('landform', 'scale'),
        landformWarpScale: n('landform', 'warpScale'),
        landformWarpFraction: n('landform', 'warpFraction'),
        landformMountainHeight: vox(n('landform', 'mountainHeight')),
        landformSeaDepthFrac: pct(n('landform', 'seaDepthFrac')),
        landformBeachWidthFrac: pct(n('landform', 'beachWidthFrac')),
        landformSnowLineFrac: pct(n('surface', 'snowLineFrac')),
        landformFlatnessAmount: pct(n('landform', 'flatnessAmount')),
        landformFlatnessScale: n('landform', 'flatnessScale'),
        landformRidged: pct(n('landform', 'ridged')),
        landformOctaves: n('landform', 'octaves'),
        landformCurve: (getParam(cfg, 'landform', 'curve') ?? []),
        // The terrace is a child kind with its own switch, so turning it off means a step of zero —
        // which is exactly what the generator reads as "smooth".
        landformTerraceStepFrac: own(cfg, 'terrace') ? pct(n('terrace', 'stepFrac')) : 0,
        landformStepSharpness: n('terrace', 'sharpness'),
        landformStepCoverage: pct(n('terrace', 'coverage')),
        landformStepMix: pct(n('terrace', 'mix')),
        landformStepMaskScale: n('terrace', 'maskScale'),
        landformStepWobble: pct(n('terrace', 'wobble')),
        landformStepWobbleScale: n('terrace', 'wobbleScale'),
        landformDetailFrequency: n('detail', 'frequency'),
        landformDetailFlat: own(cfg, 'detail') ? vox(n('detail', 'flat')) : 0,
        landformDetailSteep: own(cfg, 'detail') ? vox(n('detail', 'steep')) : 0,
        landformRockSlopeDeg: n('surface', 'rockSlopeDeg'),
        surfaceGroundMat: material(cfg, 'surface', 'ground', 'moss2'),
        surfaceBeachMat: material(cfg, 'surface', 'beach', 'sand'),
        surfaceSeabedMat: material(cfg, 'surface', 'seabed', 'gravel'),
        surfaceSteepMat: material(cfg, 'surface', 'steep', 'rock'),
        surfaceSnowMat: material(cfg, 'surface', 'snow', 'snow'),
        // A sea that is switched off has nowhere to say so in the flat config, so it says it here: a
        // waterline the writer never reaches. `sea.enabled` gating the WATER PHASE is the honest place
        // for this and it goes there when the phases are kinds (see the deferred resolver work).
        waterMaterial: material(cfg, 'sea', 'material', 'water'),
    };
    // EVERY TYPE OFF IS NO REGIONS, not "all of them equally". RegionField falls back to [1,1,1] on a
    // zero total, which is right for a malformed config and wrong for a deliberate one — so the
    // deliberate case is answered here instead of reaching it. The editor stops you getting here by
    // hand; an imported or hand-edited config still can.
    const typeWeights = REGION_TYPE_KINDS.map((r) => (own(cfg, r.kind) ? n(r.kind, 'frequency') : 0));
    const anyType = typeWeights.some((w) => w > 0);
    const region = {
        ...DEFAULT_REGION_CONFIG,
        enabled: own(cfg, 'regions') && anyType,
        cellSize: n('regions', 'cellSize'),
        jitter: pct(n('regions', 'jitter')),
        edgeDetail: n('regions', 'edgeDetail'),
        edgeWander: pct(n('regions', 'edgeWander')),
        typeWeights,
        wallMaterials: REGION_TYPE_KINDS.map((r) => material(cfg, r.kind, 'wallMaterial', r.fallbackWall)),
        // A wall with no height is no wall, which is how the kind's switch reaches a config that has
        // no toggle of its own for it.
        wallWidth: n('wall', 'width'),
        wallHeight: own(cfg, 'wall') ? vox(n('wall', 'height')) : 0,
        scatterRegions: REGION_TYPE_KINDS
            .filter((r) => own(cfg, r.kind) && scatters(cfg, r.kind))
            .map((r) => r.type),
        roadsEnabled: own(cfg, 'city') && own(cfg, 'roads'),
        roadSpacing: n('roads', 'spacing'),
        roadWidth: n('roads', 'width'),
        buildingsEnabled: own(cfg, 'city') && own(cfg, 'block'),
        blockInset: n('block', 'inset'),
        buildingMinHeight: vox(n('block', 'minHeight')),
        buildingMaxHeight: vox(n('block', 'maxHeight')),
        buildingHeightBias: n('block', 'heightBias'),
        downtownFalloff: pct(n('block', 'downtownFalloff')),
        interiorsEnabled: own(cfg, 'floor'),
        storeyHeight: n('floor', 'storeyHeight'),
        windowPitch: n('floor', 'windowPitch'),
        roomWidth: n('room', 'roomWidth'),
        corridorWidth: n('room', 'corridorWidth'),
    };
    return {
        seed: cfg.seed,
        terrainLayer: terrain,
        regionConfig: region,
        enableStamps: stamps,
        stampConfig: { distributions: stampDistributions(cfg) },
        pathsConfig: {
            enabled: own(cfg, 'paths'),
            wanderM: n('paths', 'wander'),
            halfWidthM: n('paths', 'width') / 2,
            apronM: n('paths', 'apron'),
            gradeWeight: n('paths', 'grade'),
        },
        oakLarge: {
            trunkM: n('oaksLarge', 'trunk'),
            radiusM: n('oaksLarge', 'radius'),
            rangeDeg: n('oaksLarge', 'range'),
            density: n('oaksLarge', 'density'),
            prune: pct(n('oaksLarge', 'prune')),
            foliage: getParam(cfg, 'oaksLarge', 'leaves') === true,
        },
    };
}
//# sourceMappingURL=resolveConfig.js.map